import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Typography,
  Card,
  CardContent,
  Box,
  Paper,
  Chip,
  Divider,
  Tooltip,
  CircularProgress,
  darken,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { getPaymentLinkDetails } from "../../logic/ServerCalls";
import { calculateTotalProductPrice } from "../../logic/Calculations";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

const PaymentSelection = ({
  paymentId,
  setDirectDebitState,
  switchFormState,
  labels,
  params,
  sendToCRMManualPayment,
  completeUpCareOrder,
  color,
  productsValues,
  proceedManualReview,
  payslipFiles,
  addPayslipFiles,
  removePayslipFiles,
}) => {
  // Pre-select Adyen
  const [selectedMethod, setSelectedMethod] = useState("adyen");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const paymentWindowRef = useRef(null);
  const paymentPollingRef = useRef(null);
  const isCheckingPaymentStatusRef = useRef(false);

  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualConfirmed, setManualConfirmed] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [manualReviewDialogOpen, setManualReviewDialogOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);
      }
    };
  }, []);

  const handleMethodSelection = (method) => {
    if (method === selectedMethod) return;
    setSelectedMethod(method);
    setPaymentStatus(null);
    if (method === "other") {
      setManualDialogOpen(true);
      setManualConfirmed(false);
    }
  };

  const openAdyenLink = () => {
    if (paymentId) {
      const paymentWindow = window.open(
        `https://eu.adyen.link/${paymentId}`,
        "_blank",
      );
      paymentWindowRef.current = paymentWindow;

      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);
      }

      paymentPollingRef.current = setInterval(async () => {
        if (isCheckingPaymentStatusRef.current) {
          return;
        }

        isCheckingPaymentStatusRef.current = true;

        try {
          const detail = await getPaymentLinkDetails(paymentId);

          if (detail.message.status === "completed") {
            clearInterval(paymentPollingRef.current);
            paymentPollingRef.current = null;
            setPaymentStatus("completed");

            setTimeout(() => {
              if (paymentWindow && !paymentWindow.closed) {
                paymentWindow.close();
              }
            }, 2000);
          }
        } catch (error) {
          console.error("ERROR:", error);
        } finally {
          isCheckingPaymentStatusRef.current = false;
        }
      }, 1000);
    } else {
      setPaymentStatus("error");
    }
  };

  const handleSkipPayment = async () => {
    const formType = params.formType.toLowerCase();
    const lang = params.lang.toLowerCase();

    setSkipDialogOpen(false);
    setIsProcessing(true);
    setPaymentStatus("skipped");
    sessionStorage.setItem("paymentCompleted", false);
    sessionStorage.setItem("upcareWithoutPayment", true);

    try {
      if (
        formType === "upcare" &&
        (lang === "uk" ||
          lang === "se" ||
          lang === "no" ||
          lang === "dk" ||
          lang === "jp")
      ) {
        await completeUpCareOrder();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProceed = async () => {
    const formType = params.formType.toLowerCase();
    const lang = params.lang.toLowerCase();
    if (!selectedMethod) {
      setPaymentStatus("error");
      return;
    }

    setIsProcessing(true);
    // Reset the payment status when starting a new attempt
    setPaymentStatus(null);

    try {
      if (selectedMethod === "adyen") {
        const detail = await getPaymentLinkDetails(paymentId);
        console.log("Payment Link Details:", detail.message);

        if (detail.message.status === "completed") {
          setPaymentStatus("completed");
          sessionStorage.setItem("paymentCompleted", true);
          setDirectDebitState();
          if (
            formType === "upcare" &&
            (lang === "uk" ||
              lang === "se" ||
              lang === "no" ||
              lang === "dk" ||
              lang === "jp")
          ) {
            await completeUpCareOrder();
            return;
          }
          // TODO: Extend to other Leaseplus markets once they have a transfer
          // agreement step, using the correct per-market threshold (e.g.
          // SE/NO = 60000 SEK/NOK, DK = 40000 DKK instead of 5000).
          if (
            formType === "leaseplus" &&
            (lang === "fr" || lang === "de" || lang === "uk")
          ) {
            const totalProductPrice =
              calculateTotalProductPrice(productsValues);
            if (totalProductPrice >= 5000) {
              setManualReviewDialogOpen(true);
              return;
            }
          }
          switchFormState("create_contract");
        } else {
          setPaymentStatus("pending");
        }
      } else {
        setPaymentStatus("manual");
      }
    } catch (error) {
      setPaymentStatus("error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualReviewConfirm = async () => {
    setManualReviewDialogOpen(false);
    setIsProcessing(true);
    try {
      await proceedManualReview();
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayslipFileChange = (event) => {
    const files = [...event.target.files];
    if (files.length > 0) {
      addPayslipFiles(files);
    }
    event.target.value = "";
  };

  return (
    <Box width="100%">
      {/* Manual Payment Dialog */}
      <Dialog
        open={manualDialogOpen}
        onClose={() => {
          setManualDialogOpen(false);
          setSelectedMethod("adyen");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
        >
          <Typography variant="h6" component="div">
            {labels.payment_are_you_sure}
            {/* Êtes-vous sûr de vouloir continuer sans activer le prélèvement
            automatique? */}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {labels.payment_manual_transaction_text}
            {/* Cela signifie que la transaction devra être traitée manuellement par
            HCS, ce qui signifie que le client recevra son contrat à une date
            ultérieure. */}
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {labels.payment_continue_without_text}
            {/* Si vous continuez sans domiciliation, le risque d'annulation
            prématurée du contrat en raison d'un défaut de paiement de la part
            du client est beaucoup plus élevé, ce qui vous obligera (vous, le
            revendeur) à racheter le produit à une valeur plus élevée que celle
            indiquée dans le contrat. */}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #dee2e6",
            p: 2,
          }}
        >
          <Button
            onClick={() => {
              setManualDialogOpen(false);
            }}
            variant="contained"
          >
            {labels.payment_return_to_direct}
            {/* Revenir au prélèvement automatique */}
          </Button>
          <Button
            onClick={async () => {
              setManualConfirmed(true);
              setManualDialogOpen(false);
              setSelectedMethod("other");
              sessionStorage.setItem("paymentCompleted", false);
              setIsProcessing(true);
              try {
                await sendToCRMManualPayment("manual_payment");
              } finally {
                setIsProcessing(false);
              }
            }}
            variant="contained"
            color="error"
          >
            {labels.payment_confirm_manual}
            {/* Confirmer le paiement manuel */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skip Payment Confirmation Dialog */}
      <Dialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "#fff" }}>
          <Typography variant="h6" component="div">
            {labels.payment_skip_header}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, backgroundColor: "#fff" }}>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {labels.payment_skip_text}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#fff", p: 2 }}>
          <Button
            onClick={() => setSkipDialogOpen(false)}
            variant="contained"
            color="error"
          >
            {labels.go_back}
          </Button>
          <Button
            onClick={handleSkipPayment}
            variant="contained"
            sx={{
              backgroundColor: color,
              "&:hover": {
                backgroundColor: darken(color, 0.2),
              },
            }}
          >
            {labels.payment_skip_confirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Review Dialog  */}
      <Dialog
        open={manualReviewDialogOpen}
        onClose={() => setManualReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
            {labels.manual_review_title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {labels.manual_review_text}
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
            {labels.manual_review_payslip_text}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            component="label"
            startIcon={<AttachFileIcon />}
          >
            {labels.manual_review_upload_documents}
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handlePayslipFileChange}
              hidden
            />
          </Button>
          {payslipFiles.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
              {payslipFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  size="small"
                  onDelete={() => removePayslipFiles(file)}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #dee2e6",
            p: 2,
          }}
        >
          <Button onClick={handleManualReviewConfirm} variant="contained">
            {labels.manual_review_continue}
          </Button>
        </DialogActions>
      </Dialog>
      <Card sx={{ width: "100%", boxShadow: 2, borderRadius: 1 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="500" mb={2}>
            {labels.payment_signup}
            {/* Inscrire au prélèvement automatique */}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            {/* Adyen Payment Option */}
            <Paper
              sx={{
                flex: 1, // Changed from flex: 2 to flex: 1
                border:
                  selectedMethod === "adyen"
                    ? "2px solid #1976d2"
                    : "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CreditCardIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="500">
                      {labels.adyen_payment}
                      {/* Paiement Adyen */}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {labels.adyen_payment_secure}
                      {/* Paiement sécurisé via la plateforme Adyen */}
                    </Typography>
                  </Box>
                </Box>
                <CheckCircleIcon color="primary" />
              </Box>

              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f5f8ff",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body2" mb={2}>
                  {labels.adyen_payment_secure_description}
                  {/* Adyen fournit une passerelle de paiement sécurisée pour les
                  cartes de crédit, les cartes de débit et les portefeuilles
                  numériques. */}
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={openAdyenLink}
                  startIcon={<CreditCardIcon />}
                  fullWidth
                  sx={{ mb: 1, padding: "12px 0" }}
                >
                  {labels.adyen_payment_proceed}
                  {/* Procéder au paiement sur Adyen */}
                </Button>

                {paymentStatus && (
                  <Chip
                    color={
                      paymentStatus === "completed"
                        ? "success"
                        : paymentStatus === "error"
                          ? "error"
                          : "warning"
                    }
                    label={
                      paymentStatus === "completed"
                        ? "Payment completed"
                        : paymentStatus === "error"
                          ? "Payment error"
                          : paymentStatus === "skipped"
                            ? "Continued without payment"
                            : "Complete payment before proceeding"
                    }
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Paper>

            {/* Information Box */}
            <Paper
              sx={{
                flex: 1,
                p: 2,
                backgroundColor: "#f5f8ff",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                // height: "fit-content",
              }}
            >
              <Typography variant="h6" gutterBottom>
                {labels.payment_important_information}
                {/* Informations importantes : */}
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <Typography component="li" variant="body2" paragraph>
                  {labels.payment_click_button}
                  {/* Cliquez sur le bouton bleu pour enregistrer votre mode de
                  paiement. */}
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  {labels.payment_card_implementation}
                  {/* Cela permet de mettre en place le prélèvement automatique pour
                  les paiements du leasing. */}
                </Typography>

                {/* <Typography component="li" variant="body2" paragraph>
                  {labels.payment_no_amount}
                  {/* Aucun montant ne sera prélevé avant le début officiel du
                  contrat. */}
                {/* </Typography> } */}
                <Typography component="li" variant="body2" paragraph>
                  {labels.payment_cancel_contract}
                  {/* Si vous annulez la demande de contrat, toutes les informations
                  de paiement seront automatiquement supprimées. */}
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  {labels.payment_registration_complete}
                  {/* Une fois l'enregistrement terminé, revenez sur cette page pour
                  continuer la procédure. */}
                </Typography>
                {params.formType.toLowerCase() === "upcare" && (
                  <Typography component="li" variant="body2" paragraph>
                    {labels.payment_later_note}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Manual Payment Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            {paymentStatus === "completed" ? (
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: "bold",
                }}
              >
                {labels.payment_completed_text}
              </Typography>
            ) : (
              params.formType.toLowerCase() !== "upcare" && (
                <Tooltip
                  title={labels.payment_want_to_pay_manually_tooltip}
                  // "Ce mode de paiement peut entraîner des retards, des erreurs, des frais supplémentaires et une perte de patience. Utilisez à vos risques et périls."
                >
                  <Button
                    size="small"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      textDecoration: "underline",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                    onClick={() => handleMethodSelection("other")}
                  >
                    {labels.payment_want_to_pay_manually}
                    {/* Mon client souhaite payer chaque facture de leasing manuellement
                par virement bancaire (non recommandé) */}
                  </Button>
                </Tooltip>
              )
            )}
          </Box>
        </CardContent>
      </Card>

      {/* External button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 2,
          mb: 2,
        }}
      >
        {params.formType.toLowerCase() === "upcare" &&
          paymentStatus !== "completed" &&
          (isProcessing ? (
            <Box
              sx={{
                minWidth: "350px",
                minHeight: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <CircularProgress size={42} />
            </Box>
          ) : (
            <Button
              variant="outlined"
              sx={{
                minWidth: "350px",
                minHeight: "45px",
                color: color,
                borderColor: color,
                backgroundColor: "#fff",
                "&:hover": {
                  borderColor: darken(color, 0.2),
                  color: darken(color, 0.2),
                  backgroundColor: "#fff",
                },
              }}
              onClick={() => setSkipDialogOpen(true)}
            >
              {labels.payment_skip_continue}
            </Button>
          ))}
        {((selectedMethod === "adyen" && paymentStatus === "completed") ||
          selectedMethod === "other") &&
          (isProcessing ? (
            <Box
              sx={{
                minWidth: "350px",
                minHeight: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <CircularProgress size={42} />
            </Box>
          ) : (
            <Button
              variant="contained"
              sx={{
                minWidth: "350px",
                minHeight: "45px",
                backgroundColor: color,
                "&:hover": {
                  backgroundColor: darken(color, 0.2), // Darken by 20%
                },
              }}
              onClick={handleProceed}
              disabled={!selectedMethod || isProcessing}
            >
              {labels.payment_continue}
              <SendIcon sx={{ marginLeft: "15px" }} />
            </Button>
          ))}
      </Box>
    </Box>
  );
};

PaymentSelection.propTypes = {
  paymentId: PropTypes.string.isRequired,
  setDirectDebitState: PropTypes.func.isRequired,
  switchFormState: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  sendToCRMManualPayment: PropTypes.func.isRequired,
  productsValues: PropTypes.object.isRequired,
  proceedManualReview: PropTypes.func.isRequired,
  payslipFiles: PropTypes.array.isRequired,
  addPayslipFiles: PropTypes.func.isRequired,
  removePayslipFiles: PropTypes.func.isRequired,
};

export default PaymentSelection;
